import { makeStyles } from 'tss-react/mui';

import { dFlex } from '@assets/mixins';

import { DEFAULT_FONT_FAMILY } from '@constants/common/ui.constants';

interface Params {
  hasHelperText: boolean;
  disabled: boolean;
  isError: boolean;
}

const useStyles = makeStyles<Params>()(
  (theme, { hasHelperText, disabled, isError }) => ({
    root: {
      width: '100%',
      position: 'relative',
    },

    formControl: {
      width: '100%',
    },
    disabledBox: {
      opacity: 0.48,

      '& .MuiFormHelperText-root': {
        span: {
          color: theme.secondary.s3,
        },
      },
    },
    emptyValue: {
      '& .MuiFormLabel-root': {
        background: `${theme.secondary.s1} !important`,

        '&.MuiInputLabel-sizeSmall': {
          lineHeight: '24px',
        },

        '&.MuiInputLabel-sizeLarge': {
          lineHeight: '38px',
        },
      },

      '& .Mui-focused': {
        '& .MuiFormLabel-root': {
          background: 'red !important',
        },

        '&.MuiFormLabel-root': {
          lineHeight: '24px',
        },
      },
    },
    input: {
      background: theme.secondary.s1,
      borderRadius: theme.borderRadius.md,
      transition: 'all 0.25s ease-in-out',

      '& textarea': {
        boxSizing: 'border-box',
        color: theme.secondary.s3,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '24px',

        '&::placeholder': {
          fontSize: 16,
          color: theme.secondary.s3,
          opacity: 1,
        },

        '&:focus': {
          caretColor: theme.primary.p3,
        },

        '&:hover': {
          color: theme.primary.p2,
        },
      },

      '& input': {
        boxSizing: 'border-box',
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 400,
        fontFamily: DEFAULT_FONT_FAMILY,
        color: theme.secondary.s3,
        height: 56,

        '&.MuiInputBase-inputSizeSmall': {
          height: 44,
          minHeight: 44,
        },

        '&::placeholder': {
          fontSize: 16,
          color: theme.secondary.s3,
          opacity: 1,
        },

        '&:focus': {
          caretColor: theme.primary.p3,
        },
      },

      '& .MuiOutlinedInput-root': {
        borderRadius: theme.borderRadius.md,

        '& .MuiOutlinedInput-notchedOutline ': {
          paddingLeft: '13px',

          legend: {
            fontSize: '10px',

            span: {
              height: '10px',
              padding: 0,
            },
          },
        },

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.secondary.s4,

          ...(disabled && {
            borderColor: 'transparent',
          }),
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
          color: theme.primary.p2,

          '& input': {
            color: theme.primary.p2,
          },
        },

        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          '& input': {
            color: theme.primary.p2,
          },
        },

        '&.Mui-disabled .MuiOutlinedInput-input': {
          color: theme.primary.p2,
          WebkitTextFillColor: theme.primary.p2,
        },
      },

      '&:hover': {
        '& input': {
          color: theme.primary.p2,

          '&::placeholder': {
            color: theme.primary.p2,
          },
        },

        '& .MuiSvgIcon-root': {
          color: theme.primary.p2,
        },
      },
      '&:focus': {
        color: theme.primary.p3,

        '& input': {
          color: theme.primary.p3,
        },

        '& .MuiSvgIcon-root': {
          color: theme.primary.p2,
        },
      },

      // label styles
      '&:hover .MuiFormLabel-root': {
        color: theme.primary.p2,
      },
      '& .MuiFormLabel-root': {
        color: theme.secondary.s3,
        background: theme.background.b1,
        padding: '0 4px',

        '&.Mui-focused': {
          color: theme.primary.p3,
        },

        '&.Mui-disabled': {
          color: theme.primary.p2,
        },
      },
    },
    supportText: {
      paddingTop: 4,
      lineHeight: 1,
    },
    formHelperText: {
      marginLeft: 0,
      marginTop: 0,
    },
    error: {
      right: 0,
      marginLeft: 'auto',

      '& span': {
        color: theme.accent.a6,
      },
    },
    errorBox: {
      '.MuiFormLabel-root': {
        color: theme.accent.a1,
      },

      '&:hover .MuiFormLabel-root': {
        color: theme.accent.a6,
      },

      '& .MuiFormLabel-root': {
        color: theme.accent.a6,

        '&.Mui-focused': {
          color: theme.accent.a6,
        },
      },

      '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.accent.a6,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.accent.a6,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.accent.a6} !important`,
        },
      },

      '& input': {
        color: theme.primary.p2,
        caretColor: `${theme.accent.a6} !important`,
      },
    },
    readOnlyBox: {
      '& .MuiFormControl-root': {
        '& input': {
          color: theme.primary.p3,
          caretColor: theme.primary.p3,
          cursor: 'default',
        },
      },

      '& .MuiInputBase-root.MuiOutlinedInput-root': {
        borderColor: 'transparent',
      },

      '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
      },
    },
    buttonMax: {
      textTransform: 'uppercase',
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.primary.p2,
      cursor: 'pointer',

      '&:hover': {
        opacity: '0.7',
      },

      '&:active': {
        opacity: '0.5',
      },

      ...(disabled && {
        cursor: 'unset',

        '&:hover': {
          opacity: 1,
        },

        '&:active': {
          opacity: 1,
        },
      }),
    },
    searchIcon: {
      ...dFlex(),
      color: theme.secondary.s3,
      width: 28,
    },
    actionsBox: {
      ...dFlex(),
      gap: 12,
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translate(0, -50%)',

      ...(hasHelperText &&
        isError && {
          top: '33%',
          transform: 'translate(0, -85%)',
        }),

      ...(hasHelperText &&
        !isError && {
          top: '33%',
        }),

      ...(isError &&
        !hasHelperText && {
          top: '33%',
          transform: 'translate(0, -50%)',
        }),
    },
    withDropdown: {
      '.MuiInputBase-input.MuiOutlinedInput-input': {
        paddingRight: '180px',
      },
    },
    darkForm: {
      '& .MuiFormControl-root': {
        backgroundColor: theme.background.b2,
      },

      '& .MuiFormLabel-root': {
        background: `${theme.background.b2} !important`,
      },

      ...(disabled && {
        border: 'none',

        '& input': {
          color: theme.secondary.s3,
          WebkitTextFillColor: `${theme.secondary.s3} !important`,

          '&::placeholder': {
            WebkitTextFillColor: theme.secondary.s3,
          },
        },

        '& .MuiOutlinedInput-notchedOutline ': {
          borderColor: 'transparent !important',
        },
      }),
    },
    selectedTokenBtn: {
      '&.MuiButtonBase-root': {
        gap: 4,
      },
      '& .MuiButton-icon': {
        margin: 0,
      },
    },
    tokenIcon: {
      width: 20,
      height: 20,
    },
    arrowIcon: {
      width: 20,
      height: 20,
    },
  }),
);

export default useStyles;
